import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  forecastsService,
  ShiftOptimizationConfig,
  PriorShiftPattern,
  AutoGenerateShiftsOptions,
  AutoGenerateShiftsResult,
  PlannedShift,
  PlanningSummary,
} from '../services/forecasts.service';

/**
 * Hook to optimize shifts for a forecast using interval-aligned algorithm
 *
 * Features:
 * - Interval-aligned shift generation (15-min granularity)
 * - Target occupancy optimization (default 82.5%)
 * - Schedule continuity from prior published forecasts
 * - Agent grouping strategies (MAXIMIZE, BALANCED, NONE)
 */
export function useOptimizeShifts(forecastId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (config: Omit<ShiftOptimizationConfig, 'forecastId'>) =>
      forecastsService.optimizeShifts(forecastId, config),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['forecasts', forecastId, 'shifts'] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', forecastId, 'planning-summary'] });
    },
  });
}

/**
 * Hook to fetch prior shift patterns from published forecasts
 * Used for schedule continuity when generating new shifts
 */
export function usePriorShiftPatterns(
  forecastId: string,
  options?: { priorForecastId?: string; enabled?: boolean }
) {
  return useQuery<PriorShiftPattern[]>({
    queryKey: ['forecasts', forecastId, 'prior-patterns', options?.priorForecastId],
    queryFn: () => forecastsService.getPriorShiftPatterns(forecastId, options?.priorForecastId),
    enabled: options?.enabled !== false && !!forecastId,
  });
}

/**
 * Hook to auto-generate shifts from forecast requirements
 */
export function useAutoGenerateShifts(forecastId: string) {
  const queryClient = useQueryClient();

  return useMutation<AutoGenerateShiftsResult, Error, AutoGenerateShiftsOptions>({
    mutationFn: (options) => forecastsService.autoGenerateShifts(forecastId, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forecasts', forecastId, 'shifts'] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', forecastId, 'planning-summary'] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', forecastId, 'gaps'] });
    },
  });
}

/**
 * Hook to fetch planned shifts for a forecast
 */
export function usePlannedShifts(
  forecastId: string,
  options?: { includeAllocations?: boolean; enabled?: boolean }
) {
  return useQuery<PlannedShift[]>({
    queryKey: ['forecasts', forecastId, 'shifts', options?.includeAllocations],
    queryFn: () => forecastsService.getPlannedShifts(forecastId, {
      includeAllocations: options?.includeAllocations,
    }),
    enabled: options?.enabled !== false && !!forecastId,
  });
}

/**
 * Hook to fetch planning summary for a forecast
 */
export function usePlanningSummary(forecastId: string, options?: { enabled?: boolean }) {
  return useQuery<PlanningSummary>({
    queryKey: ['forecasts', forecastId, 'planning-summary'],
    queryFn: () => forecastsService.getPlanningSummary(forecastId),
    enabled: options?.enabled !== false && !!forecastId,
  });
}

/**
 * Combined hook for shift optimization workflow
 *
 * Provides:
 * - Optimization mutation with config
 * - Prior patterns query for continuity
 * - Real-time occupancy calculations
 */
export function useShiftOptimization(forecastId: string) {
  const optimizeMutation = useOptimizeShifts(forecastId);
  const priorPatternsQuery = usePriorShiftPatterns(forecastId, { enabled: !!forecastId });
  const autoGenerateMutation = useAutoGenerateShifts(forecastId);
  const planningSummaryQuery = usePlanningSummary(forecastId, { enabled: !!forecastId });

  return {
    // Optimization
    optimize: optimizeMutation.mutateAsync,
    isOptimizing: optimizeMutation.isPending,
    optimizationResult: optimizeMutation.data,
    optimizationError: optimizeMutation.error,

    // Prior patterns for continuity
    priorPatterns: priorPatternsQuery.data ?? [],
    isLoadingPriorPatterns: priorPatternsQuery.isLoading,
    priorPatternsError: priorPatternsQuery.error,

    // Auto-generate (for compatibility with existing flow)
    autoGenerate: autoGenerateMutation.mutateAsync,
    isAutoGenerating: autoGenerateMutation.isPending,
    autoGenerateResult: autoGenerateMutation.data,
    autoGenerateError: autoGenerateMutation.error,

    // Planning summary
    planningSummary: planningSummaryQuery.data,
    isLoadingSummary: planningSummaryQuery.isLoading,

    // Helper to reset state
    reset: () => {
      optimizeMutation.reset();
      autoGenerateMutation.reset();
    },
  };
}

export default useShiftOptimization;
